import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getFullBackendUrl } from 'helper/download';
import {
  Box,
  Button,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import FileUploadButton from 'components/FileUploadButton';

import { UPLOAD_OFFER_DOCUMENT, REMOVE_OFFER_DOCUMENT, LIST_OFFER_DOCUMENTS, LIST_SELECTED_OFFER_STANDARD_DOCUMENTS } from '../pages/offers/gql';
import { EOfferSource, ViewWlOfferVersionQuery } from '__generated__/graphql';
import { userSelector } from 'helper/security';
import { getStoredGuestOfferId, getStoredGuestOfferToken } from '../pages/offers/guest-offer-storage';

const MAX_TOTAL_SIZE = 10 * 1024 * 1024;

function base64ByteLength(base64Data: string): number {
  let data = base64Data;
  if (data.startsWith('data:')) {
    const commaIndex = data.indexOf(',');
    if (commaIndex !== -1) data = data.substring(commaIndex + 1);
  }
  const padding = (data.match(/=+$/) || [''])[0].length;
  return Math.floor((data.length * 3) / 4) - padding;
}

interface OfferDocumentsProps {
  offerVersion: NonNullable<ViewWlOfferVersionQuery['viewWlOfferVersion']>,
  canEdit: boolean;
  /** Guest deeplink: append token to document download URLs. */
  guestMode?: boolean;
  onDocumentChange?: () => void;
}

/**
 * Component for managing offer documents (PDF attachments).
 * Only allows uploads for SEM bookings (WIDGET source) or Seminargo users.
 * Documents are included in confirmation emails.
 */
export const OfferDocuments: React.FC<OfferDocumentsProps> = ({ offerVersion, canEdit, guestMode, onDocumentChange }) => {
  const dispatch = useDispatch();
  const user = userSelector()!;

  const [canUpload] = useState(
    canEdit && (user.isSeminargo || offerVersion.offer.source === EOfferSource.WIDGET),
  );

  const [uploadLoading, setUploadLoading] = useState(false);

  const { data, loading, error, refetch } = useQuery(LIST_OFFER_DOCUMENTS, {
    variables: { offerId: offerVersion.offer.id },
  });

  const { data: selectedStandardData } = useQuery(LIST_SELECTED_OFFER_STANDARD_DOCUMENTS, {
    variables: { offerId: offerVersion.offer.id },
    fetchPolicy: 'network-only',
  });

  const [uploadDocument] = useMutation(UPLOAD_OFFER_DOCUMENT, {
    onCompleted: () => {
      refetch();
      setUploadLoading(false);
      dispatchMessage(dispatch, String(i18next.t('offer-documents-upload-success')));
      onDocumentChange?.();
    },
    onError: (error) => {
      setUploadLoading(false);
      if (error.graphQLErrors?.some(e => e.extensions?.code === 'ATTACHMENT_SIZE_EXCEEDED')) {
        dispatchException(dispatch, new Error(String(i18next.t('offer-standard-document-size-exceeded'))));
      } else {
        dispatchException(dispatch, error);
      }
    },
  });

  const [removeDocument] = useMutation(REMOVE_OFFER_DOCUMENT, {
    onCompleted: () => {
      refetch();
      dispatchMessage(dispatch, String(i18next.t('offer-documents-delete-success')));
      onDocumentChange?.();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const handleFileUpload = async (filename: string, mimeType: string, base64Content: string) => {
    if (!canUpload) return;

    if (mimeType !== 'application/pdf') {
      dispatchException(dispatch, new Error(String(i18next.t('offer-documents-only-pdf'))));
      return;
    }

    const existingAdHocSize = (data?.offerDocuments || []).reduce((sum: number, d: any) => sum + (d.fileSize || 0), 0);
    const selectedStandardSize = (selectedStandardData?.offerStandardDocuments || []).reduce(
      (sum: number, d: any) => sum + (d.documentFile?.fileSize || 0),
      0,
    );
    const newFileSize = base64ByteLength(base64Content);

    if (existingAdHocSize + selectedStandardSize + newFileSize > MAX_TOTAL_SIZE) {
      dispatchException(dispatch, new Error(String(i18next.t('offer-standard-document-size-exceeded'))));
      return;
    }

    setUploadLoading(true);
    uploadDocument({
      variables: {
        offerVersionId: offerVersion.id,
        filename,
        base64: base64Content,
        mimeType: 'application/pdf',
      },
    });
  };

  const handleFileDelete = async (fileId: number, filename: string) => {
    if (!canUpload) return;

    removeDocument({
      variables: { offerId: offerVersion.offer.id, fileId },
    });
  };

  const handleDownload = (fileId: number) => {
    let url = getFullBackendUrl(`/backend/spacefiles/download/${fileId}`);
    if (guestMode) {
      const token = getStoredGuestOfferToken();
      const offerId = getStoredGuestOfferId();
      if (token && offerId) {
        const params = new URLSearchParams({ token, offerId: `${offerId}` });
        url += `?${params.toString()}`;
      }
    }
    window.open(url, '_blank');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{i18next.t('offer-documents-load-error')}: {error.message}</Alert>;

  const documents = data?.offerDocuments || [];

  if (!canUpload && documents.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" mb={2}>
            <AttachFileIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              {i18next.t('offer-documents-title')}
            </Typography>
          </Box>

          {canUpload && (
            <Box mb={2}>
              <FileUploadButton
                name="offerDocument"
                accept="application/pdf"
                disabled={uploadLoading}
                startIcon={uploadLoading ? <CircularProgress size={24} /> : <UploadIcon />}
                binary={(name, type) => true}
                onChange={handleFileUpload}
                buttonProps={{
                  variant: 'outlined',
                  color: 'primary',
                }}
              >
                {i18next.t('offer-documents-upload-pdf')}
              </FileUploadButton>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                {i18next.t('offer-documents-upload-hint')}
              </Typography>
            </Box>
          )}

          {documents.length > 0 ? (
            <List>
              {documents.map((doc: any) => (
                <ListItem key={doc.id} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">{doc.originalFilename}</Typography>
                        <Chip label="PDF" size="small" color="primary" />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption">
                        {formatFileSize(doc.fileSize)} • {i18next.t('offer-documents-uploaded-at')} {new Date(doc.createdAt).toLocaleDateString(i18next.language)}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={() => handleDownload(doc.id)}
                      sx={{ mr: 1 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                    {canUpload && (
                      <ConfirmationButton
                        confirmationQuestion={i18next.t('offer-document-delete-confirm-question')}
                        confirmationTitle={i18next.t('offer-document-delete-confirm-title')}
                        color="error"
                        size="small"
                        onConfirm={() => handleFileDelete(doc.id, doc.originalFilename)}
                      >
                        <DeleteIcon />
                      </ConfirmationButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              {canUpload
                ? i18next.t('offer-documents-empty-with-upload')
                : i18next.t('offer-documents-empty-readonly')
              }
            </Alert>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};